import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../api/api';
import { useCompetence } from '../contexts/CompetenceContext';
import {
    FaTable, FaSave, FaBookmark,
    FaTimes, FaGripVertical,
    FaArrowsAltH, FaArrowsAltV, FaFilter, FaCalculator,
    FaFileExcel, FaFileCsv, FaShareAlt,
    FaSearch, FaExternalLinkAlt, FaCalendarAlt
} from 'react-icons/fa';
import './PivotTable.css';

/* ─────────────── helpers ─────────────── */
const fmt = (v) => {
    if (v == null || v === '') return '–';
    const n = Number(v);
    if (isNaN(n)) return String(v);
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const AGGFUNC_OPTIONS = [
    { value: 'soma', label: 'Soma' },
    { value: 'media', label: 'Média' },
    { value: 'contagem', label: 'Contagem' },
    { value: 'minimo', label: 'Mínimo' },
    { value: 'maximo', label: 'Máximo' },
    { value: 'percentual_total', label: '% do Total' },
];

/* ════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════ */
const PivotTablePage = () => {
    const { globalCompetence } = useCompetence();

    /* ---- field metadata ---- */
    const [dimensions, setDimensions] = useState([]);
    const [metrics, setMetrics] = useState([]);
    const [dimLabels, setDimLabels] = useState({});
    const [metricLabels, setMetricLabels] = useState({});
    const [distinctValues, setDistinctValues] = useState({});

    /* ---- competência multi-select ---- */
    const [availableCompetencias, setAvailableCompetencias] = useState([]);
    const [selectedCompetencias, setSelectedCompetencias] = useState([]);
    const [showCompSelector, setShowCompSelector] = useState(false);

    /* ---- pivot config ---- */
    const [rows, setRows] = useState(['centro_custo']);
    const [columns, setColumns] = useState([]);
    const [values, setValues] = useState([{ field: 'custo_total', aggfunc: 'soma' }]);
    const [filters, setFilters] = useState([]);

    /* ---- results ---- */
    const [data, setData] = useState([]);
    const [totals, setTotals] = useState({});
    const [rowFields, setRowFields] = useState([]);
    const [colFields, setColFields] = useState([]);
    const [loading, setLoading] = useState(false);

    /* ---- drill-down ---- */
    const [drilldown, setDrilldown] = useState(null);
    const [drillLoading, setDrillLoading] = useState(false);
    const [drillRecords, setDrillRecords] = useState([]);

    /* ---- views ---- */
    const [savedViews, setSavedViews] = useState([]);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [viewName, setViewName] = useState('');
    const [viewShared, setViewShared] = useState(false);

    /* ---- drag ---- */
    const [dragItem, setDragItem] = useState(null);
    const [dragOverZone, setDragOverZone] = useState(null);

    /* ---- auto-execute ---- */
    const autoExecTimer = useRef(null);
    const isFirstRender = useRef(true);

    /* ═══════════ Load fields on mount ═══════════ */
    useEffect(() => {
        loadFields();
        loadSavedViews();
    }, []);  // eslint-disable-line

    const loadFields = async () => {
        try {
            const res = await axios.get(`${API_URL}/pivot/fields`);
            const f = res.data.fields || {};
            setDimensions(f.dimensions || []);
            setMetrics(f.metrics || []);
            setDimLabels(f.dimension_labels || {});
            setMetricLabels(f.metric_labels || {});
            setDistinctValues(f.distinct_values || {});
            const comps = (f.competencias || []).sort();
            setAvailableCompetencias(comps);
            // Pre-select globalCompetence if set, otherwise select all
            if (globalCompetence && comps.includes(globalCompetence)) {
                setSelectedCompetencias([globalCompetence]);
            } else if (comps.length > 0) {
                setSelectedCompetencias(comps);
            }
        } catch (err) {
            console.error('Erro ao carregar campos pivot:', err);
        }
    };

    const loadSavedViews = async () => {
        try {
            const res = await axios.get(`${API_URL}/pivot/views`);
            setSavedViews(res.data.views || []);
        } catch { /* ignore */ }
    };

    /* ═══════════ Execute query ═══════════ */
    const executeQuery = useCallback(async () => {
        if (rows.length === 0 && columns.length === 0) return;
        if (values.length === 0) return;
        setLoading(true);
        try {
            const compParam = selectedCompetencias.length > 0 && selectedCompetencias.length < availableCompetencias.length
                ? selectedCompetencias : undefined;
            const body = {
                competencias: compParam,
                rows, columns, values, filters, limit: 5000
            };
            const res = await axios.post(`${API_URL}/pivot/query`, body);
            setData(res.data.data || []);
            setTotals(res.data.totals || {});
            setRowFields(res.data.row_fields || rows);
            setColFields(res.data.col_fields || columns);
        } catch (err) {
            console.error('Erro na query pivot:', err);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [globalCompetence, rows, columns, values, filters, selectedCompetencias, availableCompetencias]);

    /* ═══════════ AUTO-EXECUTE on config change ═══════════ */
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            executeQuery();
            return;
        }
        if (autoExecTimer.current) clearTimeout(autoExecTimer.current);
        autoExecTimer.current = setTimeout(() => { executeQuery(); }, 400);
        return () => { if (autoExecTimer.current) clearTimeout(autoExecTimer.current); };
    }, [rows, columns, values, filters, selectedCompetencias, executeQuery]); // eslint-disable-line

    /* ═══════════ Drill-down ═══════════ */
    const handleCellClick = async (record) => {
        const keys = {};
        [...rowFields, ...colFields].forEach(f => {
            if (record[f] != null) keys[f] = record[f];
        });
        setDrilldown(keys);
        setDrillLoading(true);
        try {
            const compParam = selectedCompetencias.length > 0 && selectedCompetencias.length < availableCompetencias.length
                ? selectedCompetencias : undefined;
            const res = await axios.post(`${API_URL}/pivot/drilldown`, {
                competencias: compParam, keys, limit: 500
            });
            setDrillRecords(res.data.records || []);
        } catch (err) {
            console.error('Erro drill-down:', err);
            setDrillRecords([]);
        } finally {
            setDrillLoading(false);
        }
    };

    /* ═══════════ Export ═══════════ */
    const handleExport = async (format) => {
        try {
            const compParam = selectedCompetencias.length > 0 && selectedCompetencias.length < availableCompetencias.length
                ? selectedCompetencias : undefined;
            const body = {
                competencias: compParam,
                rows, columns, values, filters, format, limit: 50000
            };
            const res = await axios.post(`${API_URL}/pivot/export`, body, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = format === 'excel' ? 'tabela_dinamica.xlsx' : 'tabela_dinamica.csv';
            a.click();
        } catch (err) {
            alert('Erro ao exportar: ' + (err.response?.data?.mensagem || err.message));
        }
    };

    /* ═══════════ Open in New Tab (Interactive) ═══════════ */
    const openInNewTab = () => {
        if (data.length === 0) return;

        // Build columns array with keys, labels, types
        const cols = [];
        rowFields.forEach(f => cols.push({ key: f, label: getFieldLabel(f), type: 'label' }));
        colFields.forEach(f => cols.push({ key: f, label: getFieldLabel(f), type: 'label' }));
        allValueFields.forEach(f => cols.push({ key: f, label: getFieldLabel(f), type: 'value' }));
        const hasPct = values.some(v => v.aggfunc === 'percentual_total');
        if (hasPct) allValueFields.forEach(f => cols.push({ key: f + '_pct', label: '% Total', type: 'pct' }));

        const configDesc = [
            rows.length > 0 ? 'Linhas: ' + rows.map(getFieldLabel).join(', ') : '',
            columns.length > 0 ? 'Colunas: ' + columns.map(getFieldLabel).join(', ') : '',
            values.length > 0 ? 'Valores: ' + values.map(v => getFieldLabel(v.field)).join(', ') : '',
        ].filter(Boolean).join(' | ');

        const jsonData = JSON.stringify(data);
        const jsonCols = JSON.stringify(cols);
        const jsonTotals = JSON.stringify(totals);

        const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<title>Tabela Dinâmica – SACH</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Inter','Roboto',sans-serif;background:#f0f2f5;color:#1e293b;padding:20px}
h1{font-size:1.3rem;font-weight:700;color:#6366f1;margin-bottom:4px}
.config-info{font-size:.76rem;color:#888;margin-bottom:12px}

/* Toolbar */
.toolbar{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:12px;padding:10px 14px;border-radius:10px;background:#fff;border:1px solid #e2e8f0;box-shadow:0 1px 3px rgba(0,0,0,.06)}
.toolbar input[type="text"]{padding:6px 12px;border:1px solid #d1d5db;border-radius:6px;font-size:.8rem;width:260px;transition:border-color .15s}
.toolbar input[type="text"]:focus{border-color:#6366f1;outline:none;box-shadow:0 0 0 3px rgba(99,102,241,.12)}
.toolbar .btn{padding:6px 12px;border-radius:6px;border:none;font-weight:600;font-size:.76rem;cursor:pointer;transition:all .15s;display:inline-flex;align-items:center;gap:4px}
.toolbar .btn-primary{background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff}
.toolbar .btn-primary:hover{box-shadow:0 3px 10px rgba(99,102,241,.3)}
.toolbar .btn-secondary{background:#fff;color:#333;border:1px solid #ddd}
.toolbar .btn-secondary:hover{background:#f1f1f1}
.toolbar .btn-active{background:#6366f1;color:#fff;border-color:#6366f1}
.stats-text{font-size:.74rem;color:#888;margin-left:auto}

/* Column panel */
.col-panel{margin-bottom:10px;padding:10px 14px;border-radius:10px;background:#fff;border:1px solid #e2e8f0;display:none;flex-wrap:wrap;gap:6px}
.col-panel.open{display:flex}
.col-chip{padding:4px 10px;border-radius:14px;font-size:.72rem;font-weight:500;cursor:pointer;transition:all .15s;border:1px solid #d1d5db;background:#f8fafc;color:#555}
.col-chip.active{background:#6366f1;color:#fff;border-color:#6366f1}
.col-chip:hover{transform:translateY(-1px)}

/* Table */
.table-wrap{background:#fff;border-radius:12px;border:1px solid #e2e8f0;overflow:auto;max-height:calc(100vh - 220px);box-shadow:0 2px 8px rgba(0,0,0,.06)}
table{width:100%;border-collapse:collapse;font-size:.82rem}
thead th{background:linear-gradient(135deg,#f8fafc,#eef2ff);color:#4338ca;font-weight:700;font-size:.72rem;text-transform:uppercase;letter-spacing:.3px;padding:8px 12px;border-bottom:2px solid #c7d2fe;white-space:nowrap;text-align:left;position:sticky;top:0;z-index:2;cursor:pointer;user-select:none;transition:background .15s}
thead th:hover{background:linear-gradient(135deg,#eef2ff,#e0e7ff)}
thead th .sort-icon{margin-left:4px;font-size:.6rem;opacity:.5}
thead th.sorted .sort-icon{opacity:1;color:#6366f1}
.filter-row th{padding:4px 6px;background:#f8fafc;border-bottom:1px solid #e2e8f0;position:sticky;top:34px;z-index:2}
.filter-row input{width:100%;padding:4px 8px;border:1px solid #e2e8f0;border-radius:4px;font-size:.72rem;background:#fff}
.filter-row input:focus{border-color:#6366f1;outline:none}
tbody td{padding:7px 12px;border-bottom:1px solid #f0f0f0;white-space:nowrap}
tbody tr:hover{background:rgba(99,102,241,.04)}
.cell-value{text-align:right;font-variant-numeric:tabular-nums;font-weight:500}
.cell-label{font-weight:500}
.cell-pct{text-align:right;color:#6366f1;font-weight:500}
tfoot td{font-weight:700;background:#f8fafc;border-top:2px solid #c7d2fe;padding:8px 12px}
.hidden-col{display:none}

/* Footer */
.footer{display:flex;justify-content:space-between;align-items:center;margin-top:8px;font-size:.74rem;color:#888}

@media print{body{padding:0;background:#fff}.toolbar,.col-panel,.filter-row{display:none!important}}
</style>
</head>
<body>

<h1>📊 Tabela Dinâmica – SACH</h1>
<p class="config-info">${configDesc} | Competência: ${globalCompetence || 'Todas'}</p>

<div class="toolbar">
  <input type="text" id="globalSearch" placeholder="🔍 Pesquisar em todos os campos..." oninput="applyFilters()">
  <button class="btn btn-secondary" onclick="togglePanel()">⚙ Colunas</button>
  <button class="btn btn-secondary" onclick="clearFilters()">✕ Limpar Filtros</button>
  <button class="btn btn-secondary" onclick="window.print()">🖨 Imprimir</button>
  <span class="stats-text" id="statsText"></span>
</div>

<div class="col-panel" id="colPanel"></div>

<div class="table-wrap">
  <table id="pivotTable">
    <thead>
      <tr id="headerRow"></tr>
      <tr class="filter-row" id="filterRow"></tr>
    </thead>
    <tbody id="tableBody"></tbody>
    <tfoot id="tableFoot"></tfoot>
  </table>
</div>

<div class="footer">
  <span id="footerStats"></span>
  <span>Gerado em ${new Date().toLocaleString('pt-BR')}</span>
</div>

<script>
const DATA = ${jsonData};
const COLS = ${jsonCols};
const TOTALS = ${jsonTotals};

let visibleCols = COLS.map((_,i) => i);
let sortCol = -1;
let sortDir = 'asc';
let colFilters = COLS.map(() => '');

function fmt(v) {
  if (v == null || v === '') return '–';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('pt-BR', {minimumFractionDigits:2, maximumFractionDigits:2});
}

function renderHeaders() {
  const hr = document.getElementById('headerRow');
  const fr = document.getElementById('filterRow');
  hr.innerHTML = '';
  fr.innerHTML = '';
  COLS.forEach((c, i) => {
    const th = document.createElement('th');
    th.textContent = c.label;
    th.className = (sortCol === i ? 'sorted' : '') + (visibleCols.includes(i) ? '' : ' hidden-col');
    if (c.type === 'value' || c.type === 'pct') th.style.textAlign = 'right';
    const icon = document.createElement('span');
    icon.className = 'sort-icon';
    icon.textContent = sortCol === i ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ↕';
    th.appendChild(icon);
    th.onclick = () => { if (sortCol === i) { sortDir = sortDir === 'asc' ? 'desc' : 'asc'; } else { sortCol = i; sortDir = 'asc'; } renderAll(); };
    hr.appendChild(th);

    const fth = document.createElement('th');
    fth.className = visibleCols.includes(i) ? '' : 'hidden-col';
    const inp = document.createElement('input');
    inp.type = 'text';
    inp.placeholder = 'Filtrar...';
    inp.value = colFilters[i];
    inp.oninput = (e) => { colFilters[i] = e.target.value; applyFilters(); };
    fth.appendChild(inp);
    fr.appendChild(fth);
  });
}

function getFiltered() {
  const globalQ = (document.getElementById('globalSearch').value || '').toLowerCase();
  return DATA.filter(rec => {
    if (globalQ) {
      const match = COLS.some((c,i) => visibleCols.includes(i) && String(rec[c.key] || '').toLowerCase().includes(globalQ));
      if (!match) return false;
    }
    return COLS.every((c, i) => {
      if (!colFilters[i]) return true;
      return String(rec[c.key] || '').toLowerCase().includes(colFilters[i].toLowerCase());
    });
  });
}

function getSorted(rows) {
  if (sortCol < 0) return rows;
  const key = COLS[sortCol].key;
  const isNum = COLS[sortCol].type !== 'label';
  return [...rows].sort((a, b) => {
    let va = a[key], vb = b[key];
    if (isNum) { va = Number(va) || 0; vb = Number(vb) || 0; }
    else { va = String(va || '').toLowerCase(); vb = String(vb || '').toLowerCase(); }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
}

function renderBody() {
  const filtered = getFiltered();
  const sorted = getSorted(filtered);
  const tbody = document.getElementById('tableBody');
  tbody.innerHTML = '';
  sorted.forEach(rec => {
    const tr = document.createElement('tr');
    COLS.forEach((c, i) => {
      const td = document.createElement('td');
      td.className = (c.type === 'value' ? 'cell-value' : c.type === 'pct' ? 'cell-pct' : 'cell-label') + (visibleCols.includes(i) ? '' : ' hidden-col');
      if (c.type === 'pct') td.textContent = rec[c.key] != null ? rec[c.key] + '%' : '–';
      else if (c.type === 'value') td.textContent = fmt(rec[c.key]);
      else td.textContent = rec[c.key] || '–';
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  });
  document.getElementById('statsText').textContent = filtered.length + ' de ' + DATA.length + ' registros';
  document.getElementById('footerStats').textContent = filtered.length + ' registros exibidos';

  // Footer
  const tfoot = document.getElementById('tableFoot');
  tfoot.innerHTML = '';
  const tr = document.createElement('tr');
  COLS.forEach((c, i) => {
    const td = document.createElement('td');
    td.className = visibleCols.includes(i) ? '' : 'hidden-col';
    if (c.type === 'value') { td.style.textAlign = 'right'; td.textContent = fmt(TOTALS[c.key]); }
    else if (c.type === 'pct') { td.style.textAlign = 'right'; td.style.color = '#6366f1'; td.textContent = '100%'; }
    else if (i === 0) td.textContent = 'TOTAL';
    tr.appendChild(td);
  });
  tfoot.appendChild(tr);
}

function renderColPanel() {
  const panel = document.getElementById('colPanel');
  panel.innerHTML = '';
  COLS.forEach((c, i) => {
    const chip = document.createElement('span');
    chip.className = 'col-chip' + (visibleCols.includes(i) ? ' active' : '');
    chip.textContent = c.label;
    chip.onclick = () => {
      if (visibleCols.includes(i)) visibleCols = visibleCols.filter(x => x !== i);
      else visibleCols.push(i);
      renderAll();
    };
    panel.appendChild(chip);
  });
}

function togglePanel() {
  document.getElementById('colPanel').classList.toggle('open');
}

function clearFilters() {
  colFilters = COLS.map(() => '');
  document.getElementById('globalSearch').value = '';
  renderAll();
}

function applyFilters() { renderBody(); }

function renderAll() {
  renderHeaders();
  renderColPanel();
  renderBody();
}

renderAll();
<\/script>
</body>
</html>`;

        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
    };

    /* ═══════════ Views ═══════════ */
    const saveView = async () => {
        if (!viewName.trim()) return;
        try {
            await axios.post(`${API_URL}/pivot/views`, {
                name: viewName, config: { rows, columns, values, filters }, shared: viewShared
            });
            setShowSaveModal(false);
            setViewName('');
            loadSavedViews();
        } catch { alert('Erro ao salvar visão.'); }
    };

    const loadView = (view) => {
        const c = view.config || {};
        setRows(c.rows || []);
        setColumns(c.columns || []);
        setValues(c.values || [{ field: 'valor_total', aggfunc: 'soma' }]);
        setFilters(c.filters || []);
    };

    const deleteView = async (id, e) => {
        e.stopPropagation();
        if (!window.confirm('Remover esta visão?')) return;
        try { await axios.delete(`${API_URL}/pivot/views/${id}`); loadSavedViews(); } catch { }
    };

    /* ═══════════ Drag & Drop ═══════════ */
    const onDragStart = (e, field) => {
        setDragItem({ field });
        e.dataTransfer.effectAllowed = 'move';
    };

    const onDragOver = (e, zone) => { e.preventDefault(); setDragOverZone(zone); };
    const onDragLeave = () => setDragOverZone(null);

    const onDrop = (e, zone) => {
        e.preventDefault();
        setDragOverZone(null);
        if (!dragItem) return;
        const { field } = dragItem;

        // Remove from all zones first
        setRows(prev => prev.filter(f => f !== field));
        setColumns(prev => prev.filter(f => f !== field));
        setValues(prev => prev.filter(v => v.field !== field));
        setFilters(prev => prev.filter(f => f.field !== field));

        if (zone === 'rows') setRows(prev => [...prev, field]);
        else if (zone === 'columns') setColumns(prev => [...prev, field]);
        else if (zone === 'values') setValues(prev => [...prev, { field, aggfunc: 'soma' }]);
        else if (zone === 'filters') {
            const defaultVal = (distinctValues[field] || [])[0] || '';
            setFilters(prev => [...prev, { field, operator: 'eq', value: defaultVal }]);
        }
        setDragItem(null);
    };

    const removeFromZone = (field, zone) => {
        if (zone === 'rows') setRows(prev => prev.filter(f => f !== field));
        else if (zone === 'columns') setColumns(prev => prev.filter(f => f !== field));
        else if (zone === 'values') setValues(prev => prev.filter(v => v.field !== field));
        else if (zone === 'filters') setFilters(prev => prev.filter(f => f.field !== field));
    };

    const getFieldLabel = (f) => dimLabels[f] || metricLabels[f] || f;

    /* ═══════════ RENDER ═══════════ */
    const allValueFields = values.map(v => v.field);
    const drilldownCols = drillRecords.length > 0 ? Object.keys(drillRecords[0]) : [];

    // Available fields (not yet in any zone)
    const availableDimensions = dimensions.filter(d =>
        !rows.includes(d) && !columns.includes(d) && !filters.some(f => f.field === d)
    );
    const availableMetrics = metrics.filter(m => !values.some(v => v.field === m));

    return (
        <div className="pivot-page">
            {/* ── Header ── */}
            <div className="pivot-header">
                <h1><FaTable style={{ marginRight: 8 }} /> Tabela Dinâmica</h1>
                <p className="subtitle">Análise multidimensional com rastreabilidade total — atualiza automaticamente</p>
            </div>

            {/* ── Saved Views ── */}
            {savedViews.length > 0 && (
                <div className="saved-views-list">
                    <span style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginRight: 4, alignSelf: 'center' }}>
                        <FaBookmark /> Visões:
                    </span>
                    {savedViews.map(v => (
                        <div key={v.id} className="saved-view-chip" onClick={() => loadView(v)} title={`Criado por ${v.created_by}`}>
                            {v.shared && <FaShareAlt size={10} />}
                            {v.name}
                            <span className="delete-view" onClick={(e) => deleteView(v.id, e)}>✕</span>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Toolbar ── */}
            <div className="pivot-toolbar">
                <button className="btn btn-secondary" onClick={() => handleExport('excel')}>
                    <FaFileExcel /> Excel
                </button>
                <button className="btn btn-secondary" onClick={() => handleExport('csv')}>
                    <FaFileCsv /> CSV
                </button>
                <button className="btn btn-secondary" onClick={() => setShowSaveModal(true)}>
                    <FaSave /> Salvar Visão
                </button>
                {data.length > 0 && (
                    <button className="btn btn-primary" onClick={openInNewTab}>
                        <FaExternalLinkAlt /> Nova Aba
                    </button>
                )}
                <div style={{ flex: 1 }} />
                {loading && (
                    <span style={{ fontSize: '.76rem', color: '#6366f1', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span className="inline-spinner" /> Atualizando...
                    </span>
                )}
                <div style={{ position: 'relative' }}>
                    <button
                        className={`btn ${showCompSelector ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setShowCompSelector(!showCompSelector)}
                        style={{ fontSize: '.76rem' }}
                    >
                        <FaCalendarAlt />
                        {selectedCompetencias.length === availableCompetencias.length || selectedCompetencias.length === 0
                            ? 'Todas Competências'
                            : `${selectedCompetencias.length} Competência(s)`}
                    </button>
                    {showCompSelector && (
                        <div className="comp-selector-panel">
                            <div className="comp-selector-header">
                                <button className="comp-btn" onClick={() => setSelectedCompetencias([...availableCompetencias])}>Todas</button>
                                <button className="comp-btn" onClick={() => setSelectedCompetencias([])}>Nenhuma</button>
                            </div>
                            <div className="comp-chips">
                                {availableCompetencias.map(c => (
                                    <span
                                        key={c}
                                        className={`comp-chip ${selectedCompetencias.includes(c) ? 'active' : ''}`}
                                        onClick={() => {
                                            setSelectedCompetencias(prev =>
                                                prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c].sort()
                                            );
                                        }}
                                    >
                                        {c}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ════════ CONFIG ZONES (top, colored, 4 columns) ════════ */}
            <div className="pivot-config-section">
                {/* ROWS – Indigo */}
                <div className={`pivot-drop-zone zone-rows ${dragOverZone === 'rows' ? 'drag-over' : ''}`}
                    onDragOver={(e) => onDragOver(e, 'rows')} onDragLeave={onDragLeave} onDrop={(e) => onDrop(e, 'rows')}>
                    <h4><FaArrowsAltV className="zone-icon" /> Linhas</h4>
                    <div className="zone-chips">
                        {rows.map(f => (
                            <span key={f} className="zone-chip row-chip" draggable onDragStart={(e) => onDragStart(e, f)}>
                                {getFieldLabel(f)}
                                <span className="remove-chip" onClick={() => removeFromZone(f, 'rows')}>✕</span>
                            </span>
                        ))}
                        {rows.length === 0 && <span className="zone-placeholder">Arraste dimensões aqui</span>}
                    </div>
                </div>

                {/* COLUMNS – Amber */}
                <div className={`pivot-drop-zone zone-columns ${dragOverZone === 'columns' ? 'drag-over' : ''}`}
                    onDragOver={(e) => onDragOver(e, 'columns')} onDragLeave={onDragLeave} onDrop={(e) => onDrop(e, 'columns')}>
                    <h4><FaArrowsAltH className="zone-icon" /> Colunas</h4>
                    <div className="zone-chips">
                        {columns.map(f => (
                            <span key={f} className="zone-chip col-chip" draggable onDragStart={(e) => onDragStart(e, f)}>
                                {getFieldLabel(f)}
                                <span className="remove-chip" onClick={() => removeFromZone(f, 'columns')}>✕</span>
                            </span>
                        ))}
                        {columns.length === 0 && <span className="zone-placeholder">Arraste dimensões aqui</span>}
                    </div>
                </div>

                {/* VALUES – Green */}
                <div className={`pivot-drop-zone zone-values ${dragOverZone === 'values' ? 'drag-over' : ''}`}
                    onDragOver={(e) => onDragOver(e, 'values')} onDragLeave={onDragLeave} onDrop={(e) => onDrop(e, 'values')}>
                    <h4><FaCalculator className="zone-icon" /> Valores</h4>
                    <div className="zone-chips">
                        {values.map((v, i) => (
                            <span key={v.field} className="zone-chip val-chip">
                                {getFieldLabel(v.field)}
                                <select value={v.aggfunc} onChange={(e) => {
                                    const nv = [...values]; nv[i] = { ...nv[i], aggfunc: e.target.value }; setValues(nv);
                                }}>
                                    {AGGFUNC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                                <span className="remove-chip" onClick={() => removeFromZone(v.field, 'values')}>✕</span>
                            </span>
                        ))}
                        {values.length === 0 && <span className="zone-placeholder">Arraste métricas aqui</span>}
                    </div>
                </div>

                {/* FILTERS – Rose */}
                <div className={`pivot-drop-zone zone-filters ${dragOverZone === 'filters' ? 'drag-over' : ''}`}
                    onDragOver={(e) => onDragOver(e, 'filters')} onDragLeave={onDragLeave} onDrop={(e) => onDrop(e, 'filters')}>
                    <h4><FaFilter className="zone-icon" /> Filtros</h4>
                    <div className="zone-chips">
                        {filters.map((f, i) => (
                            <span key={f.field} className="zone-chip filter-chip">
                                {getFieldLabel(f.field)}
                                <select value={f.operator || 'eq'} onChange={(e) => {
                                    const nf = [...filters]; nf[i] = { ...nf[i], operator: e.target.value }; setFilters(nf);
                                }} style={{ width: 44 }}>
                                    <option value="eq">=</option><option value="neq">≠</option>
                                    <option value="contains">∋</option><option value="gt">&gt;</option><option value="lt">&lt;</option>
                                </select>
                                {distinctValues[f.field] ? (
                                    <select value={f.value || ''} onChange={(e) => {
                                        const nf = [...filters]; nf[i] = { ...nf[i], value: e.target.value }; setFilters(nf);
                                    }} style={{ maxWidth: 90 }}>
                                        <option value="">Todos</option>
                                        {distinctValues[f.field].map(v => <option key={v} value={v}>{v}</option>)}
                                    </select>
                                ) : (
                                    <input type="text" value={f.value || ''} onChange={(e) => {
                                        const nf = [...filters]; nf[i] = { ...nf[i], value: e.target.value }; setFilters(nf);
                                    }} placeholder="valor" style={{ width: 60, padding: '2px 4px', fontSize: '.7rem', borderRadius: 4, border: '1px solid rgba(255,255,255,.4)', background: 'rgba(255,255,255,.2)', color: '#fff' }} />
                                )}
                                <span className="remove-chip" onClick={() => removeFromZone(f.field, 'filters')}>✕</span>
                            </span>
                        ))}
                        {filters.length === 0 && <span className="zone-placeholder">Arraste para filtrar</span>}
                    </div>
                </div>
            </div>

            {/* ════════ AVAILABLE FIELDS BAR ════════ */}
            {(availableDimensions.length > 0 || availableMetrics.length > 0) && (
                <div className="pivot-fields-bar">
                    {availableDimensions.length > 0 && (
                        <div className="field-group">
                            <h4>📋 Dimensões</h4>
                            <div className="field-items">
                                {availableDimensions.map(d => (
                                    <div key={d} className="pivot-field-item dimension" draggable onDragStart={(e) => onDragStart(e, d)}>
                                        <FaGripVertical className="field-icon" /> {getFieldLabel(d)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {availableMetrics.length > 0 && (
                        <div className="field-group">
                            <h4>📈 Métricas</h4>
                            <div className="field-items">
                                {availableMetrics.map(m => (
                                    <div key={m} className="pivot-field-item metric" draggable onDragStart={(e) => onDragStart(e, m)}>
                                        <FaGripVertical className="field-icon" /> {getFieldLabel(m)}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ════════ RESULTS TABLE (full width) ════════ */}
            {loading && data.length === 0 && (
                <div className="pivot-loading">
                    <div className="spinner" />
                    Calculando tabela dinâmica...
                </div>
            )}

            {!loading && data.length === 0 && (
                <div className="pivot-empty-state">
                    <div className="empty-icon">📊</div>
                    <p style={{ fontSize: '1rem', fontWeight: 500 }}>
                        {values.length === 0
                            ? 'Arraste ao menos uma métrica para a zona Valores'
                            : 'Nenhum dado encontrado para a configuração atual'}
                    </p>
                    <p style={{ fontSize: '.82rem' }}>
                        Arraste campos da barra acima para as zonas coloridas
                    </p>
                </div>
            )}

            {data.length > 0 && (
                <>
                    <div className="pivot-grid-wrapper" style={{ opacity: loading ? 0.5 : 1 }}>
                        <table className="pivot-grid">
                            <thead>
                                <tr>
                                    {rowFields.map(f => <th key={f}>{getFieldLabel(f)}</th>)}
                                    {colFields.map(f => <th key={f}>{getFieldLabel(f)}</th>)}
                                    {allValueFields.map(f => <th key={f} style={{ textAlign: 'right' }}>{getFieldLabel(f)}</th>)}
                                    {values.some(v => v.aggfunc === 'percentual_total') && allValueFields.map(f => (
                                        <th key={f + '_pct'} style={{ textAlign: 'right' }}>% Total</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((rec, idx) => (
                                    <tr key={idx}>
                                        {rowFields.map(f => <td key={f} className="cell-label">{rec[f] || '–'}</td>)}
                                        {colFields.map(f => <td key={f} className="cell-label">{rec[f] || '–'}</td>)}
                                        {allValueFields.map(f => (
                                            <td key={f} className="cell-value" onClick={() => handleCellClick(rec)} title="Clique para detalhar">
                                                {fmt(rec[f])}
                                            </td>
                                        ))}
                                        {values.some(v => v.aggfunc === 'percentual_total') && allValueFields.map(f => (
                                            <td key={f + '_pct'} className="cell-value" style={{ color: '#6366f1' }}>
                                                {rec[f + '_pct'] != null ? rec[f + '_pct'] + '%' : '–'}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    {rowFields.map((f, i) => <td key={f}>{i === 0 ? 'TOTAL' : ''}</td>)}
                                    {colFields.map(f => <td key={f}></td>)}
                                    {allValueFields.map(f => <td key={f} style={{ textAlign: 'right' }}>{fmt(totals[f])}</td>)}
                                    {values.some(v => v.aggfunc === 'percentual_total') && allValueFields.map(f => (
                                        <td key={f + '_pct'} style={{ textAlign: 'right', color: '#6366f1' }}>100%</td>
                                    ))}
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <div className="pivot-stats-bar">
                        <span>{data.length} registros</span>
                        <span>Clique em qualquer valor para drill-down</span>
                    </div>
                </>
            )}

            {/* ──────── Drill-down Modal ──────── */}
            {drilldown && (
                <div className="drilldown-overlay" onClick={() => setDrilldown(null)}>
                    <div className="drilldown-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2><FaSearch style={{ marginRight: 8 }} /> Detalhamento (Drill-down)</h2>
                            <button className="close-btn" onClick={() => setDrilldown(null)}><FaTimes /></button>
                        </div>
                        <div style={{ padding: '8px 24px', fontSize: '.76rem', color: 'var(--text-muted)', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {Object.entries(drilldown).map(([k, v]) => (
                                <span key={k} style={{ background: 'rgba(99,102,241,.08)', padding: '3px 10px', borderRadius: 12 }}>
                                    <strong>{getFieldLabel(k)}:</strong> {v}
                                </span>
                            ))}
                        </div>
                        <div className="modal-body">
                            {drillLoading ? (
                                <div className="pivot-loading"><div className="spinner" /> Carregando...</div>
                            ) : drillRecords.length === 0 ? (
                                <p style={{ textAlign: 'center', color: '#999', padding: 40 }}>Nenhum registro encontrado.</p>
                            ) : (
                                <table>
                                    <thead><tr>{drilldownCols.map(c => <th key={c}>{getFieldLabel(c)}</th>)}</tr></thead>
                                    <tbody>
                                        {drillRecords.map((rec, i) => (
                                            <tr key={i}>
                                                {drilldownCols.map(c => (
                                                    <td key={c}>{typeof rec[c] === 'number' ? fmt(rec[c]) : (rec[c] || '–')}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="modal-footer">
                            <span>{drillRecords.length} registros de origem</span>
                            <span style={{ fontSize: '.7rem' }}>Rastreabilidade: source / source_id</span>
                        </div>
                    </div>
                </div>
            )}

            {/* ──────── Save View Modal ──────── */}
            {showSaveModal && (
                <div className="save-modal-overlay" onClick={() => setShowSaveModal(false)}>
                    <div className="save-modal" onClick={e => e.stopPropagation()}>
                        <h3><FaSave style={{ marginRight: 8 }} /> Salvar Visão</h3>
                        <input type="text" placeholder="Nome da visão (ex: Custos por CC)" value={viewName} onChange={e => setViewName(e.target.value)} autoFocus />
                        <label>
                            <input type="checkbox" checked={viewShared} onChange={e => setViewShared(e.target.checked)} />
                            Compartilhar com outros usuários
                        </label>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={() => setShowSaveModal(false)}>Cancelar</button>
                            <button className="btn btn-primary" onClick={saveView}>Salvar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PivotTablePage;
